import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, Clock, ArrowRight, Code2, Sliders, Mic2, Headphones, Wrench, Sparkles, Flame } from "lucide-react";

/**
 * Jobs — careers / open roles page for producers + engineers.
 *
 * Brutalist editorial layout — numeric kickers, vermilion accents, sharp
 * sectioned roles list with a contact CTA. No public ATS yet, so the
 * "apply" CTA opens a pre-filled mailto with role info.
 */

const ROLES = [
  {
    id: "senior-audio-engineer",
    num: "01",
    icon: Sliders,
    title: "Senior Audio Engineer",
    team: "Studio",
    type: "Full-time",
    location: "Remote · US / EU",
    summary:
      "Own the playback pipeline end-to-end. iTunes / Deezer / YouTube fallback orchestration, lossless roadmap, ReplayGain normalisation, and the move from 30s previews to full-track playback for premium users.",
    chips: ["FFmpeg", "WebAudio", "DRM-free", "ReplayGain"],
  },
  {
    id: "music-producer-in-residence",
    num: "02",
    icon: Mic2,
    title: "Music Producer In-Residence",
    team: "Originals",
    type: "Contract · 6 mo",
    location: "Remote",
    summary:
      "Produce 4–6 1o1sound Originals per quarter with the artists we sign. You bring the ears, we bring the studio time, mix engineers and the release plumbing.",
    chips: ["Pro Tools / Ableton", "Mix-ready", "Genre-fluent"],
  },
  {
    id: "frontend-engineer",
    num: "03",
    icon: Code2,
    title: "Frontend Engineer (React / Framer Motion)",
    team: "Player",
    type: "Full-time",
    location: "Remote · global",
    summary:
      "You'll own the Now Playing surface, queue panel, lyrics view and the brutalist 1o1 design system. We care a lot about latency, drag feel, and the smallest details of motion.",
    chips: ["React 18", "Framer Motion", "Tailwind", "PWA / MediaSession"],
  },
  {
    id: "backend-engineer",
    num: "04",
    icon: Wrench,
    title: "Backend Engineer (Python / FastAPI)",
    team: "Platform",
    type: "Full-time",
    location: "Remote · global",
    summary:
      "Stripe Connect payouts, the catalog ledger, ad/promo HMAC signing, charts cache, and the AI upcoming-release tracker. Heavy MongoDB and httpx fan-out work.",
    chips: ["FastAPI", "MongoDB", "Stripe Connect", "Motor"],
  },
  {
    id: "ml-engineer-discovery",
    num: "05",
    icon: Sparkles,
    title: "ML Engineer — Discovery & Hotspot",
    team: "Discovery",
    type: "Full-time",
    location: "Remote · US / EU",
    summary:
      "Make Hotspot smarter. Embedding-based recommendation, daily rising-artist detection, and upcoming-release prediction across iTunes / public APIs.",
    chips: ["Embeddings", "Vector search", "LLM-augmented metadata"],
  },
  {
    id: "ar-scout",
    num: "06",
    icon: Headphones,
    title: "A&R Scout",
    team: "Label",
    type: "Full-time · or contract",
    location: "NYC · LA · ATL · London",
    summary:
      "Discover and sign 8–12 independent artists a year. Work directly with our A&R Dashboard (already in production) to track hotspot trajectories and follower velocity.",
    chips: ["Genre coverage", "Negotiation", "Touring ecosystem"],
  },
];

const RoleCard = ({ role }) => {
  const Icon = role.icon;
  const subject = encodeURIComponent(`Application — ${role.title}`);
  const body = encodeURIComponent(
    `Hi 1o1sound team,\n\nI'd like to apply for the ${role.title} role (${role.team} team).\n\nLinks (portfolio, GitHub, SoundCloud, etc.):\n\nA short note on why this role:\n\nThanks,\n`
  );
  return (
    <article
      data-testid={`job-card-${role.id}`}
      className="group relative bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 hover:border-vermilion/50 rounded-sm p-6 sm:p-7 transition"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <span className="text-[10px] tracking-[0.28em] font-black text-vermilion">{role.num}</span>
          <div className="w-9 h-9 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-vermilion/15 group-hover:border-vermilion transition">
            <Icon size={16} className="text-white/80 group-hover:text-vermilion transition" strokeWidth={2.2} />
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/40 shrink-0">{role.team}</p>
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-black uppercase tracking-tight leading-tight mb-2">
        {role.title}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed mb-4">{role.summary}</p>
      <div className="flex flex-wrap gap-3 text-[11px] text-white/45 mb-5">
        <span className="inline-flex items-center gap-1.5"><Clock size={11} /> {role.type}</span>
        <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {role.location}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {role.chips.map((c) => (
          <span
            key={c}
            className="text-[10px] uppercase tracking-[0.14em] font-bold text-white/65 border border-white/10 rounded-sm px-2 py-1"
          >
            {c}
          </span>
        ))}
      </div>
      <a
        data-testid={`job-apply-${role.id}`}
        href={`mailto:jobs@1o1soundapp.net?subject=${subject}&body=${body}`}
        className="inline-flex items-center gap-2 text-vermilion text-[12px] font-black uppercase tracking-[0.18em] group-hover:gap-3 transition-all"
      >
        Apply <ArrowRight size={14} />
      </a>
    </article>
  );
};

const Jobs = () => {
  const [filter, setFilter] = useState("all");
  const teams = ["all", ...Array.from(new Set(ROLES.map((r) => r.team)))];
  const filtered = filter === "all" ? ROLES : ROLES.filter((r) => r.team === filter);

  return (
    <div data-testid="jobs-page" className="relative">
      {/* Hero */}
      <section className="relative pt-6 pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-2 right-0 w-[380px] h-[380px] opacity-20 rotate-12"
          style={{ background: "repeating-linear-gradient(45deg, #E84525 0 6px, transparent 6px 14px)" }}
        />
        <p className="text-[11px] uppercase tracking-[0.28em] font-black mb-3 flex items-center gap-2">
          <span className="text-vermilion">01</span>
          <span className="text-white/40">Producers · Engineers · Hire-grade music people</span>
        </p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.92] mb-5 max-w-4xl">
          Join the <span className="text-vermilion">studio</span>.
        </h1>
        <p className="text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl">
          We're hiring small, senior, and remote-first. You'll work directly with the
          artists shipping music on the platform — no committees, no roadmap-by-deck,
          no taste-by-spreadsheet.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/55 border border-white/10 rounded-sm px-3 py-1.5">
            <Briefcase size={11} /> {ROLES.length} open roles
          </span>
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/55 border border-white/10 rounded-sm px-3 py-1.5">
            <MapPin size={11} /> Remote-first
          </span>
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/55 border border-white/10 rounded-sm px-3 py-1.5">
            <Sparkles size={11} /> Equity in every role
          </span>
        </div>
      </section>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-8 border-y border-white/8 py-4">
        {teams.map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            data-testid={`jobs-filter-${t}`}
            className={`text-[11px] font-black uppercase tracking-[0.18em] px-3 py-1.5 rounded-sm border transition ${
              filter === t
                ? "bg-vermilion text-white border-vermilion"
                : "text-white/55 border-white/10 hover:text-white hover:border-white/30"
            }`}
          >
            {t === "all" ? "All teams" : t}
          </button>
        ))}
      </div>

      {/* Roles grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-20">
        {filtered.map((role) => (
          <RoleCard key={role.id} role={role} />
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-white/40 col-span-2 py-12 text-center">
            No roles open on this team right now. Mail us anyway:{" "}
            <a href="mailto:jobs@1o1soundapp.net" className="text-vermilion underline">
              jobs@1o1soundapp.net
            </a>
            .
          </p>
        )}
      </section>

      {/* No fit? mail us anyway */}
      <section className="border-t border-white/10 pt-12 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.28em] font-black mb-3 text-vermilion">
              Don't see your role?
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-3">
              Pitch us anyway.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-xl">
              We hire by ear, not by req. If you're great at something we'd be lucky
              to have on the team — and you can articulate why 1o1sound is the place
              for it — send us a note.
            </p>
          </div>
          <a
            href="mailto:jobs@1o1soundapp.net?subject=I%20want%20to%20work%20at%201o1sound"
            data-testid="jobs-general-cta"
            className="inline-flex items-center justify-center gap-2 bg-vermilion hover:bg-vermilion-hover text-white text-[12px] font-black uppercase tracking-[0.12em] px-5 py-4 rounded-sm transition shadow-[0_12px_36px_-8px_rgba(232,69,37,0.5)]"
          >
            <Flame size={14} /> Mail us
          </a>
        </div>
        <div className="mt-10">
          <Link
            to="/about"
            className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/45 hover:text-white inline-flex items-center gap-2"
          >
            ← Back to about
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Jobs;
